import { useEffect, useState, useRef, Component } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import { StoreAssociateShell } from "@/components/StoreAssociateShell";
import { Toaster } from "sonner";
import { User, LogOut, Shield, Loader2 } from "lucide-react";
import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/lib/i18n";
import { asset } from "@/lib/siteAssets";
import { initAnalytics, track } from "@/lib/analytics";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CartSheet } from "@/components/CartSheet";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { AuthModal } from "@/components/AuthModal";
import { SetPasswordGate } from "@/components/SetPasswordGate";
import { NotificationPanel } from "@/components/NotificationPanel";
import { AdminChat } from "@/components/AdminChat";
import { ReviewNudge } from "@/components/ReviewNudge";
import { HomePage } from "@/pages/HomePage";
import { AboutPage } from "@/pages/AboutPage";
import { ProductsPage } from "@/pages/ProductsPage";
import { ContactPage } from "@/pages/ContactPage";
import { OffersPage } from "@/pages/OffersPage";
import { MyOrdersPage } from "@/pages/MyOrdersPage";
import { TrackOrderPage } from "@/pages/TrackOrderPage";
import { AdminPage } from "@/pages/AdminPage";
import { CheckoutPage } from "@/pages/CheckoutPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { TermsPage, PrivacyPage, ShippingPolicyPage, RefundPolicyPage, ReturnPolicyPage, CancellationPolicyPage } from "@/pages/PolicyPages";
import { PaymentStatusPage } from "@/pages/PaymentStatusPage";
import { firebaseReady } from "@/lib/firebase";
import { RemoteConfigProvider, useRemoteConfig } from "@/contexts/RemoteConfigContext";
import { CartProvider, useCartContext } from "@/contexts/CartContext";

const TOASTER_OPTS = {
  position: "top-center",
  toastOptions: {
    style: {
      background: "#F5E6CC",
      color: "#2E1A12",
      border: "1px solid rgba(90,45,29,0.25)",
      borderRadius: 2,
      boxShadow: "0 4px 16px rgba(90,45,29,0.12)",
      fontFamily: "inherit",
    },
    classNames: {
      title: "text-[#5A2D1D] font-semibold",
      description: "text-[#2E1A12]/70 text-xs",
      success: "border-[#556B2F]/40",
      error: "border-[#7A3B25]/40",
    },
  },
};

const ConfigBanner = () => {
  if (firebaseReady) return null;
  return (
    <div
      data-testid="firebase-config-banner"
      className="bg-[#7A3B25] text-[#F5E6CC] text-xs sm:text-sm py-2 px-4 text-center font-medium"
    >
      <span className="font-bold">Firebase not connected.</span> Paste your config in <code className="bg-black/20 px-1 rounded-sm">frontend/.env</code> to enable accounts, orders & admin.
    </div>
  );
};

// Maps a route to a plain-English page name, mirroring ADMIN_TAB_EVENT_LABELS
// in AdminPage.jsx — so the live Mixpanel feed reads "Viewed Shipping
// Policy" instead of a generic "page_viewed" row you have to open to
// identify. /order/:id is dynamic (a real order UUID), so it's matched by
// prefix rather than listed exactly.
const PAGE_EVENT_LABELS = {
  "/": "Home Page",
  "/about": "About Page",
  "/products": "Products Page",
  "/contact": "Contact Page",
  "/offers": "Offers Page",
  "/checkout": "Checkout Page",
  "/my-orders": "My Orders Page",
  "/track-order": "Track Order Page",
  "/profile": "Profile Page",
  "/reset-password": "Reset Password Page",
  "/terms-and-conditions": "Terms & Conditions Page",
  "/privacy-policy": "Privacy Policy Page",
  "/shipping-policy": "Shipping Policy Page",
  "/refund-policy": "Refund Policy Page",
  "/return-policy": "Return Policy Page",
  "/cancellation-policy": "Cancellation Policy Page",
  "/admin": "Admin Dashboard Page",
  "/payment-status": "Payment Status Page",
};

function pageEventLabel(path) {
  if (PAGE_EVENT_LABELS[path]) return PAGE_EVENT_LABELS[path];
  if (path.startsWith("/order/")) return "Order Tracking Page";
  return path || "Unknown Page";
}

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Target section may not be in the DOM yet on the first paint after a
      // cross-page navigation (e.g. /about#testimonials) — wait a tick.
      const id = setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        else window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }, 50);
      return () => clearTimeout(id);
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
};

// Slim top bar shown to admin-only users (replaces site header)
const AdminBar = ({ onAuthOpen }) => {
  const { user, profile, signOut, isSuperAdmin } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const initials = profile?.name
    ? profile.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
    : user?.email?.[0]?.toUpperCase() || "A";

  return (
    <header className="sticky top-0 z-40 bg-[#F5E6CC]/95 backdrop-blur-md border-b border-[#5A2D1D]/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-12">
          {/* Brand */}
          <Link to="/admin" className="flex items-center gap-2">
            <img
              src={asset("brand/logo-v2.webp")}
              alt="Annapurna Home Pickles"
              className="w-7 h-7 rounded-full object-cover shadow-sm"
            />
            <span className="font-display text-sm font-bold text-[#5A2D1D] hidden sm:inline">
              Annapurna <span className="text-[#556B2F] font-semibold">{isSuperAdmin ? "Super Admin" : "Admin"}</span>
            </span>
          </Link>

          {/* Right controls */}
          <div className="flex items-center gap-1.5">
            <NotificationPanel />

            {user ? (
              <div className="relative">
                <button
                  onClick={() => setMenuOpen((v) => !v)}
                  className="inline-flex items-center gap-2 border border-[#5A2D1D]/30 px-2 py-1 rounded-full hover:bg-[#5A2D1D]/5 transition-colors"
                >
                  {/* Avatar circle */}
                  <div className="w-6 h-6 rounded-full bg-[#5A2D1D] text-[#F5E6CC] grid place-items-center text-[10px] font-bold flex-shrink-0">
                    {initials}
                  </div>
                  <span className="text-xs font-semibold text-[#5A2D1D] max-w-[100px] truncate hidden sm:inline">
                    {profile?.name || user.email?.split("@")[0]}
                  </span>
                </button>

                {menuOpen && (
                  <div
                    className="absolute right-0 top-full mt-1 w-64 bg-[#F5E6CC] border border-[#5A2D1D]/25 rounded-sm shadow-xl z-50"
                    onMouseLeave={() => setMenuOpen(false)}
                  >
                    {/* Profile card */}
                    <div className="px-4 py-3 border-b border-[#5A2D1D]/10">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#5A2D1D] text-[#F5E6CC] grid place-items-center text-sm font-bold flex-shrink-0">
                          {initials}
                        </div>
                        <div className="min-w-0">
                          {profile?.name && (
                            <div className="font-bold text-sm text-[#2E1A12] truncate">{profile.name}</div>
                          )}
                          <div className="text-[11px] text-[#2E1A12]/55 truncate">{user.email}</div>
                          {profile?.phone && (
                            <div className="text-[11px] text-[#2E1A12]/55">{profile.phone}</div>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 inline-flex items-center gap-1 bg-[#556B2F]/15 text-[#556B2F] text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm">
                        <Shield className="w-3 h-3" /> {isSuperAdmin ? "Super Admin" : "Admin"}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="py-1">
                      <button
                        onClick={() => { setMenuOpen(false); signOut(); }}
                        className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#5A2D1D]/8 text-[#2E1A12] transition-colors"
                      >
                        <LogOut className="w-3.5 h-3.5 text-[#5A2D1D]/60" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={onAuthOpen}
                className="inline-flex items-center gap-1.5 border border-[#5A2D1D]/30 px-3 py-1.5 rounded-full text-xs font-semibold text-[#5A2D1D] hover:bg-[#5A2D1D]/5"
              >
                <User className="w-3.5 h-3.5" /> Sign In
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

// Layout for admin-only mode: slim bar + AdminPage fills screen, no footer
const AdminShell = () => {
  const [authOpen, setAuthOpen] = useState(false);
  return (
    <>
      <ConfigBanner />
      <AdminBar onAuthOpen={() => setAuthOpen(true)} />
      <main className="min-h-screen">
        <Routes>
          <Route path="/admin" element={<AdminPage onAuthOpen={() => setAuthOpen(true)} />} />
          <Route path="/order/:id" element={<TrackOrderPage />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
      <AdminChat />
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
      <Toaster {...TOASTER_OPTS} offset={{ top: "60px" }} />
    </>
  );
};

const CustomerShellInner = () => {
  const [cartOpen, setCartOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const cart = useCartContext();

  // Capture a referral code from the URL (?ref=CODE) once on first load —
  // read again at signup time (AuthContext.signUpEmail) rather than passed
  // as a prop, since the customer may browse for a while before signing up.
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) { try { sessionStorage.setItem("referral_code", ref); } catch {} }
  }, []);

  return (
    <>
      <ConfigBanner />
      <Header
        cartCount={cart.count}
        onCartOpen={() => setCartOpen(true)}
        onAuthOpen={() => setAuthOpen(true)}
      />
      <main className="min-h-[40vh]">
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route
            path="/products"
            element={
              <ProductsPage
                onAdd={(p, size, source) => {
                  cart.addItem(p, size);
                  setCartOpen(true);
                  track("product_added_to_cart", {
                    product_id: p.id,
                    product_name: p.name,
                    product_slug: p.slug,
                    size: size.label,
                    price: size.price,
                    quantity: 1,
                    category: p.category,
                    in_stock: p.in_stock,
                    cart_item_count_before: cart.count,
                    source: source || "product_card",
                  });
                }}
                onAuthOpen={() => setAuthOpen(true)}
                onCartOpen={() => setCartOpen(true)}
              />
            }
          />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/offers" element={<OffersPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/my-orders" element={<MyOrdersPage onAuthOpen={() => setAuthOpen(true)} />} />
          <Route path="/track-order" element={<TrackOrderPage />} />
          <Route path="/order/:id" element={<TrackOrderPage />} />
          <Route path="/profile" element={<ProfilePage onAuthOpen={() => setAuthOpen(true)} />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/terms-and-conditions" element={<TermsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPage />} />
          <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
          <Route path="/return-policy" element={<ReturnPolicyPage />} />
          <Route path="/cancellation-policy" element={<CancellationPolicyPage />} />
          <Route path="/payment-status" element={<PaymentStatusPage />} />
          <Route path="/admin" element={<AdminPage onAuthOpen={() => setAuthOpen(true)} />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <WhatsAppFloat />
      <ReviewNudge />
      <CartSheet
        open={cartOpen}
        onOpenChange={setCartOpen}
        items={cart.items}
        total={cart.total}
        updateQty={cart.updateQty}
        removeItem={cart.removeItem}
        onAuthOpen={() => setAuthOpen(true)}
      />
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
      <Toaster {...TOASTER_OPTS} offset={{ top: "96px" }} />
    </>
  );
};

// Layout for regular users (and admins with customer flows enabled)
const CustomerShell = () => (
  <CartProvider>
    <CustomerShellInner />
  </CartProvider>
);

const Shell = () => {
  const { pathname } = useLocation();
  const { isAdmin, isStoreAssociate, user, loading, needsPasswordSetup } = useAuth();
  const { enableAdminAndCustomerFlows } = useRemoteConfig();

  // SPA — there's no full page reload to hook into, so page views are
  // tracked here off route changes. Fires on the way OUT of a page (not on
  // arrival), so it can carry duration_seconds — same one event per page
  // visit as an arrival-only design, just timed to also report real
  // screen-time instead of needing a second event for that.
  const pageEntryRef = useRef({ path: pathname, at: Date.now() });
  // Mobile browsers can fire `pagehide` more than once for the same page
  // visit (e.g. backgrounding/locking the screen doesn't always mean the
  // user actually left) — without this guard, every extra firing sent
  // another duplicate page_viewed for the same page. Sentinel for "already
  // flushed the exit-event for the page currently in pageEntryRef" — reset
  // only when a real route change starts a new page visit.
  const flushedRef = useRef(false);

  const flushPageView = () => {
    if (flushedRef.current) return;
    const prev = pageEntryRef.current;
    if (!prev.path) return; // defensive — never send an event with no page
    track(`Viewed ${pageEventLabel(prev.path)}`, {
      page_path: prev.path,
      duration_seconds: Math.round((Date.now() - prev.at) / 1000),
    });
    flushedRef.current = true;
  };

  useEffect(() => {
    const prev = pageEntryRef.current;
    if (prev.path !== pathname) {
      flushPageView();
      pageEntryRef.current = { path: pathname, at: Date.now() };
      flushedRef.current = false;
    }
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Closing the tab / navigating off-site doesn't trigger a React Router
  // change, so the session's last page needs its own flush. pagehide (not
  // visibilitychange) is deliberate — visibilitychange also fires on a mere
  // tab switch the user might come straight back to, which would double
  // count that page's time once they actually leave for real.
  useEffect(() => {
    window.addEventListener("pagehide", flushPageView);
    return () => window.removeEventListener("pagehide", flushPageView);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const flowEmails = enableAdminAndCustomerFlows
    ? enableAdminAndCustomerFlows.split(",").map((e) => e.trim().toLowerCase()).filter(Boolean)
    : [];
  const adminOnlyMode = isAdmin && !flowEmails.includes(user?.email?.toLowerCase() || "");
  const onStorePath = pathname.startsWith("/store");

  // Wait for auth to resolve before choosing layout to avoid flash
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5E6CC] grid place-items-center">
        <Loader2 className="w-8 h-8 text-[#5A2D1D] animate-spin" />
      </div>
    );
  }

  // Google-only sign-ins must set a password before they can use the app.
  if (needsPasswordSetup) return <SetPasswordGate />;

  // Store associates get only the POS — never the customer site or admin dashboard.
  if (isStoreAssociate || onStorePath) return <StoreAssociateShell />;

  return adminOnlyMode ? <AdminShell /> : <CustomerShell />;
};

const NotFound = () => (
  <div className="min-h-[50vh] grid place-items-center px-4">
    <div className="bg-[#F5E6CC] border border-[#5A2D1D]/25 rounded-sm p-10 max-w-md text-center">
      <div className="font-display text-3xl font-bold text-[#5A2D1D] mb-2">Page not found</div>
      <Link to="/" className="text-[#556B2F] font-bold hover:underline">← Back to home</Link>
    </div>
  </div>
);

// Without this, any uncaught render error (e.g. a hook returning null before
// data loads) unmounts the entire app to a blank white screen with no way
// back except a hard reload — this catches that and offers a recovery button.
class ErrorBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error("App crashed:", error, info); }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen grid place-items-center px-4 bg-[#F5E6CC]">
        <div className="bg-white/60 border border-[#5A2D1D]/25 rounded-sm p-8 max-w-sm w-full text-center">
          <div className="font-display text-xl font-bold text-[#5A2D1D] mb-2">Something went wrong</div>
          <p className="text-sm text-[#2E1A12]/70 mb-5">Please reload the page. If this keeps happening, contact us on WhatsApp.</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-5 py-2.5 rounded-full font-semibold text-sm transition-colors"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}

export default function App() {
  useEffect(() => { initAnalytics(); }, []);

  return (
    <div className="App min-h-screen">
      <ErrorBoundary>
        <BrowserRouter>
          <LanguageProvider>
            <RemoteConfigProvider>
              <AuthProvider>
                <Shell />
              </AuthProvider>
            </RemoteConfigProvider>
          </LanguageProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}
